import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  Button,
  Avatar,
  Chip,
  Pagination,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Search,
  ChatBubbleOutline,
  FavoriteBorder,
  Share,
  FormatQuote,
  LocalOffer,
  PhotoCamera,
} from '@mui/icons-material';
import { blogPosts, blogCategories, popularFeeds } from '../data';
import SafeImage from './SafeImage';
import { BlogPost, BlogCategory, PopularFeed } from '../types';
import HeroSection from './HeroSection';
import BlogSidebar from './BlogSidebar';

export default function BlogPage() {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const postsPerPage = 6;

  // Test image paths
  React.useEffect(() => {
    //console.log('=== PROCESS ENV TEST ===');
   // console.log('PUBLIC_URL:', process.env.PUBLIC_URL);
    //console.log('NODE_ENV:', process.env.NODE_ENV);
    
    // Test different path formats
    const paths = [
      '/assets/images/burger.png',
      `${process.env.PUBLIC_URL || ''}/assets/images/burger.png`,
      './assets/images/burger.png',
      'assets/images/burger.png'
    ];
    
    paths.forEach((path, index) => {
      //console.log(`Testing path ${index + 1}: ${path}`);
      const testImg = new Image();
      //testImg.onload = () => console.log(`✅ SUCCESS: Path ${index + 1} worked`, path);
     // testImg.onerror = () => console.log(`❌ FAILED: Path ${index + 1} failed`, path);
      testImg.src = path;
    });
  }, []);

  // Filter posts based on search and categories
  const filteredPosts: BlogPost[] = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategories.length === 0 || 
                          selectedCategories.includes(post.categorySlug);
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const paginatedPosts: BlogPost[] = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handleCategoryChange = (categorySlug: string) => {
    setSelectedCategories(prev => 
      prev.includes(categorySlug) 
        ? prev.filter(cat => cat !== categorySlug)
        : [...prev, categorySlug]
    );
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {/* Header */}
        <HeroSection
                title="BLOG PAGE"
                description="Discover delicious recipes, cooking tips"
              />
      
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Blog Posts */}
            <Grid container spacing={4}>
              {paginatedPosts.map((post) => (
                <Grid item xs={12} key={post.id}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <SafeImage
                      src={`/assets/images/${post.image}`}
                      alt={`${post.title} - ${post.category} food image`}
                      fallback={`/assets/images/fallback-${post.id}.jpg`}
                      sx={{
                        width: { xs: '100%', md: 300 },
                        height: { xs: 200, md: 'auto' },
                      }}
                    />
                    <CardContent sx={{ flex: 1, p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            bgcolor: '#2e7d32',
                            color: 'white',
                            fontWeight: 500,
                            mr: 1,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {post.date} • {post.readTime}
                        </Typography>
                      </Box>
                      
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {post.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {post.excerpt}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ChatBubbleOutline fontSize="small" />
                          <Typography variant="caption">{post.comments}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FavoriteBorder fontSize="small" />
                          <Typography variant="caption">{post.likes}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Share fontSize="small" />
                          <Typography variant="caption">Share</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Testimonial */}
            <Paper
              sx={{
                mt: 6,
                p: 4,
                bgcolor: '#2e7d32',
                color: 'white',
                textAlign: 'center',
                borderRadius: 2,
              }}
            >
              <FormatQuote sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
              <Typography variant="h6" fontStyle="italic" gutterBottom>
                "Gudiya Cooking transformed our family dinners! Their recipes are easy to follow and absolutely delicious."
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                - Sarah Johnson, Happy Customer
              </Typography>
            </Paper>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          </Grid>

          {/* Sidebar (extracted) */}
          <BlogSidebar
            searchTerm={searchTerm}
            onSearch={handleSearch}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
          />
        </Grid>
      </Container>
    </Box>
  );
}
